<template>
  <div class="wrapper">
    <div class="wrapper__filters">
      <a-input-search
        class="wrapper__search"
        :placeholder="$t('search_filter.placeholder')"
        v-model="searchValue"
        @input="handleInputSearch"
      >
        <template #enterButton>
          <a-button :icon="searching ? 'loading' : 'search'" />
        </template>
      </a-input-search>
      <a-button
        v-if="searchValue"
        class="wrapper__reset-filters-btn"
        type="primary"
        @click="resetFilters"
      >
        <a-icon type="undo" />
        {{ $t("common.reset_filters") }}
      </a-button>
    </div>

    <a-table
      :dataSource="results"
      :columns="columnsConfig"
      :pagination="pagination"
      size="middle"
      rowKey="uuid"
      :loading="fetching"
      :scroll="{ x: true }"
      @change="handleTableChange"
    >
      <template #status="status, record">
        <div
          class="wrapper__status"
          :class="`wrapper__status--${INVOICE_STATUSES_MAP[status].classModifier}`"
        >
          {{ INVOICE_STATUSES_MAP[status].label }}
        </div>

        <div
          v-if="status === 'NEW' && record.expire_at"
          class="wrapper__expire-date"
        >
          {{ `${$t("tariffs.invoices.table.expiration_in")} ${formatDate(record.expire_at)}` }}
        </div>
      </template>

      <a-button
        slot="action"
        slot-scope="pdfAbsoluteUrl"
        shape="circle"
        type="dashed"
        @click="openLinkInNewTab(pdfAbsoluteUrl)"
      >
        <a-icon type="download" />
      </a-button>
    </a-table>
  </div>
</template>

<script>
import moment from "moment"

import paginationConfigs from "@/consts/paginationConfigs"
import i18n from "@/i18n"
import { getInvoices } from "@/services/tariffsService.js"
import getPriceWithCurrency from "@/utils/getPriceWithCurrency"
import makeQuery from "@/utils/makeQuery"

const SEARCH_DELAY = 200
const INVOICE_STATUSES_MAP = {
  PAID: {
    label: i18n.t("tariffs.invoices.paid"),
    classModifier: "paid"
  },
  NEW: {
    label: i18n.t("tariffs.invoices.new"),
    classModifier: "new"
  },
  CANCELED: {
    label: i18n.t("tariffs.invoices.canceled"),
    classModifier: "canceled"
  },
  REMOVED: {
    label: i18n.t("tariffs.invoices.removed"),
    classModifier: "removed"
  }
}

const formatDateTime = (date) => (date ? moment(date).format("DD.MM.YYYY, HH:mm") : "")
const formatDate = (date) => (date ? moment(date).format("DD.MM.YYYY") : "")
const createColumnsConfig = (options = {}) => {
  const { sortedInfo = {} } = options

  return [
    {
      title: i18n.t("tariffs.invoices.table.created_at"),
      dataIndex: "created_at",
      width: 130,
      sorter: true,
      customRender: formatDateTime,
      sortOrder: sortedInfo.columnKey === "created_at" && sortedInfo.order,
      fixed: "left"
    },
    {
      title: i18n.t("tariffs.invoices.table.invoice"),
      dataIndex: "code",
      width: 95,
      fixed: "left"
    },
    { title: i18n.t("tariffs.invoices.table.payer"), dataIndex: "payer", width: 320 },
    {
      title: i18n.t("tariffs.invoices.table.sum"),
      dataIndex: "amount_with_vat",
      width: 120,
      customRender: (sum) => getPriceWithCurrency(sum)
    },
    {
      title: i18n.t("tariffs.invoices.table.sum_without_vat"),
      dataIndex: "amount_without_vat",
      width: 120,
      customRender: (sum) => getPriceWithCurrency(sum)
    },
    {
      title: i18n.t("tariffs.invoices.table.vat"),
      dataIndex: "vat",
      width: 120,
      customRender: (sum) => getPriceWithCurrency(sum)
    },
    {
      title: i18n.t("tariffs.invoices.table.enrollment_date"),
      dataIndex: "paid_at",
      width: 130,
      customRender: formatDateTime
    },
    {
      title: i18n.t("tariffs.invoices.table.status"),
      dataIndex: "status",
      width: 160,
      scopedSlots: { customRender: "status" },
      fixed: "right"
    },
    { scopedSlots: { customRender: "action" }, dataIndex: "pdf", width: 55, fixed: "right" }
  ]
}

export default {
  name: "TarrifInvoices",

  metaInfo() {
    return {
      titleTemplate: `%s - ${this.$t("tariffs.invoices.title")}`
    }
  },

  beforeCreate() {
    const initialPage = Number(this.$route.query.page)

    if (!initialPage) {
      const queryMixin = { ...this.$route.query, page: 1 }

      this.$router.replace({ query: queryMixin })
    }
  },

  created() {
    this.fetchPage(this.currentRoutePage)
  },

  data() {
    return {
      INVOICE_STATUSES_MAP,
      results: [],
      sortedInfo: {},
      fetching: false,
      pagination: {
        pageSize: paginationConfigs.getPageSize("tariffs-invoices"),
        page: paginationConfigs.default.page || 1,
        showQuickJumper: true,
        showSizeChanger: true,
        showTotal: paginationConfigs.default.showTotal,
        total: 0
      },
      searchTimerId: null,
      searchValue: "",
      searching: false,
      abortController: undefined
    }
  },

  computed: {
    columnsConfig() {
      return createColumnsConfig({ sortedInfo: this.sortedInfo })
    },
    currentRoutePage() {
      const page = Number(this.$route.query.page)
      const isValidPage = !isNaN(page) && page > 0

      return isValidPage ? page : 1
    }
  },

  methods: {
    formatDateTime,
    formatDate,

    resetFilters() {
      this.searchValue = ""
      this.fetchPage(1)
    },
    handleInputSearch(value) {
      clearTimeout(this.searchTimerId)

      if (value) {
        this.searchTimerId = setTimeout(() => {
          this.fetchPage(1, value.target.value)
        }, SEARCH_DELAY)
      }
    },
    openLinkInNewTab(fileUrl) {
      const link = document.createElement("a")

      link.setAttribute("href", fileUrl)
      link.setAttribute("target", "_blank")

      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    },
    handleTableChange(pagination, _, sorter) {
      this.sortedInfo = sorter
      this.pagination = pagination

      if (this.currentRoutePage !== pagination.current) {
        const queryMixin = { ...this.$route.query, page: pagination.current }

        this.$router.push({ query: queryMixin })
      }

      this.fetchPage(this.currentRoutePage)
    },
    async fetchPage(page, searchValue) {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = undefined
      }

      this.abortController = new AbortController()

      const query = makeQuery({ sorter: this.sortedInfo })

      this.searching = Boolean(searchValue)
      this.fetching = true

      if (searchValue) {
        query.search = searchValue
      }

      try {
        if (this._productsCancelToken) {
          this._productsCancelToken()

          delete this._productsCancelToken
        }

        const executor = (cancel) => (this._productsCancelToken = cancel)
        const pageSize = this.pagination.pageSize

        paginationConfigs.setPaginationSize("tariffs-invoices", pageSize)
        const response = await getInvoices({
          pageSize,
          page,
          query,
          executor,
          signal: this.abortController.signal
        })
        const { results, count } = response.data

        this.results = results
        this.pagination.total = count
      } finally {
        this.fetching = false
        this.searching = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;

  &__filters {
    margin-bottom: 24px;
    display: flex;
  }

  &__search {
    width: 400px;
  }

  &__reset-filters-btn {
    margin-left: 8px;
  }

  &__expire-date {
    color: $gray-color-7;
  }

  &__status {
    display: inline-block;
    position: relative;
    padding-left: calc(6px + 10px);

    &::before {
      width: 6px;
      height: 6px;
      border-radius: 3px;
      content: "";
      display: inline-block;
      position: absolute;
      top: calc(50% - 3px);
      left: 0;
    }

    &--paid {
      &::before {
        background-color: #52c41a;
      }
    }

    &--canceled,
    &--removed {
      &::before {
        background-color: #ff0000;
      }
    }

    &--new {
      &::before {
        background-color: $brand-color-6;
      }
    }
  }
}
</style>
